import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  ClipboardCheck, CheckCircle2, AlertTriangle, CircleSlash, Wallet,
  Loader2, Search, Send, Coins, Receipt, ChevronDown,
} from "lucide-react";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

const STATUS_META = {
  pending:   { label: "Pending",   tone: "border-amber-300   bg-amber-50    text-amber-900",   icon: ClipboardCheck },
  confirmed: { label: "Confirmed", tone: "border-emerald-300 bg-emerald-50  text-emerald-900", icon: CheckCircle2 },
  disputed:  { label: "Disputed",  tone: "border-rose-300    bg-rose-50     text-rose-900",    icon: AlertTriangle },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${m.tone}`} data-testid={`pmc-sv-status-${status}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </Badge>
  );
}

/**
 * Per-employee salary breakup popover.
 *
 * Surfaces *how* the system arrived at "system_net_pay" so a PMC supervisor
 * can confidently confirm or dispute the paid amount. All values are
 * pro-rated for payable_days and match the Salary Report XLSX exactly.
 *
 * Numbers fall back to 0 for legacy rows published before the breakup
 * fields were added — admin should re-publish to refresh those rows.
 */
function SalaryBreakdown({ row, testidSuffix = "" }) {
  const basic = Number(row.basic_da) || 0;
  const hra = Number(row.hra) || 0;
  const other = Number(row.other_allowances) || 0;
  const grossSnap = Number(row.system_gross) || 0;
  const gross = grossSnap || (basic + hra + other);
  const pf = Number(row.pf_deduction) || 0;
  const esi = Number(row.esi_deduction) || 0;
  const pt = Number(row.pt_deduction) || 0;
  const tdSnap = Number(row.total_deductions) || 0;
  const totalDeductions = tdSnap || (pf + esi + pt);
  const net = Number(row.system_net_pay) || (gross - totalDeductions);
  const legacy = basic === 0 && hra === 0 && other === 0 && pf === 0 && esi === 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-1.5 text-[11px] text-sky-700 hover:bg-sky-50 hover:text-sky-800"
          data-testid={`pmc-sv-breakup-trigger${testidSuffix}`}
        >
          <Receipt className="mr-1 h-3 w-3" /> Breakup <ChevronDown className="ml-0.5 h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[320px] p-0 text-xs"
        data-testid={`pmc-sv-breakup-popover${testidSuffix}`}
      >
        <div className="border-b bg-slate-50 px-3 py-2">
          <div className="font-medium text-slate-800">Salary breakup</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">
            {row.employee_name} <span className="font-mono">· {row.employee_code}</span>
          </div>
          <div className="mt-0.5 text-[10px] text-slate-500">
            Working days: <span className="font-mono">{row.payable_days}/{row.total_days}</span>
          </div>
        </div>

        <div className="px-3 py-2">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-emerald-700">Earnings</div>
          <BreakupRow label="Basic + DA" v={basic} />
          <BreakupRow label="HRA" v={hra} />
          <BreakupRow label="Other allowances" v={other} />
          <BreakupRow label="Gross wages" v={gross} bold borderTop />
        </div>

        <div className="border-t px-3 py-2">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-rose-700">Deductions</div>
          <BreakupRow label="PF (employee)" v={pf} negative />
          <BreakupRow label="ESI (employee)" v={esi} negative />
          <BreakupRow label="Professional Tax" v={pt} negative />
          <BreakupRow label="Total deductions" v={totalDeductions} bold borderTop negative />
        </div>

        <div className="border-t bg-emerald-50/60 px-3 py-2">
          <BreakupRow label="Net pay (system)" v={net} bold tone="emerald" />
          {row.claimed_paid_amount != null ? (
            <BreakupRow label="Claimed as paid (contractor)" v={Number(row.claimed_paid_amount) || 0} />
          ) : (
            <div className="mt-0.5 flex justify-between text-[11px]">
              <span className="text-muted-foreground">Claimed as paid (contractor)</span>
              <span className="text-muted-foreground italic">Not claimed</span>
            </div>
          )}
        </div>

        {legacy ? (
          <div className="border-t border-amber-200 bg-amber-50 px-3 py-1.5 text-[10px] text-amber-900">
            <AlertTriangle className="mr-1 inline h-3 w-3" />
            Detailed breakup not available for this row. Ask the admin to re-publish this month&apos;s salaries to refresh.
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function BreakupRow({ label, v, bold, borderTop, negative, tone }) {
  const colour = tone === "emerald" ? "text-emerald-900" : negative ? "text-rose-800" : "text-slate-800";
  return (
    <div className={`flex justify-between py-0.5 ${borderTop ? "mt-1 border-t pt-1" : ""}`}>
      <span className={`${bold ? "font-medium" : ""} text-slate-600`}>{label}</span>
      <span className={`tabular-nums ${bold ? "font-semibold" : ""} ${colour}`}>
        {negative ? "− " : ""}₹{fmtINR(v)}
      </span>
    </div>
  );
}

function monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map((x) => parseInt(x, 10));
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
}

export default function PmcSalaryVerificationPage() {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | confirmed | disputed
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // {row, mode}
  // Park selection step — when a single park is assigned, we auto-select it.
  // Otherwise the supervisor first picks the park, then sees its employees.
  const [selectedParkId, setSelectedParkId] = useState("");

  const loadMonths = async () => {
    try {
      const res = await api.get("/pmc/salary-verifications/months").then((r) => r.data);
      setMonths(res.months || []);
      if (!month && (res.months || []).length > 0) {
        setMonth(res.months[0].month);
      }
    } catch (e) {
      toast.error("Failed to load months");
    }
  };

  const loadRows = async (m) => {
    if (!m) return;
    setLoading(true);
    try {
      const res = await api.get(`/pmc/salary-verifications?month=${m}`).then((r) => r.data);
      setData(res);
    } catch (e) {
      toast.error("Failed to load verification rows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (month) loadRows(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  // All parks available in this month's data (irrespective of search/filter).
  // This is the "park selector" data source — one card per park with summary.
  const parkSummaries = useMemo(() => {
    if (!data?.rows) return [];
    const map = new Map();
    for (const r of data.rows) {
      const key = r.park_id || "_";
      if (!map.has(key)) {
        map.set(key, {
          park_id: r.park_id,
          park_code: r.park_code,
          park_name: r.park_name,
          pending: 0, confirmed: 0, disputed: 0, total: 0,
          claimed_total: 0, paid_total: 0, system_total: 0,
        });
      }
      const p = map.get(key);
      p.total += 1;
      p[r.status || "pending"] = (p[r.status || "pending"] || 0) + 1;
      if (r.claimed_paid_amount != null) p.claimed_total += Number(r.claimed_paid_amount) || 0;
      if (r.actual_paid_amount != null) p.paid_total += Number(r.actual_paid_amount) || 0;
      if (r.system_net_pay != null) p.system_total += Number(r.system_net_pay) || 0;
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.park_code || "").localeCompare(String(b.park_code || "")),
    );
  }, [data]);

  // Auto-select the only park if just one is assigned for this month.
  useEffect(() => {
    if (!selectedParkId && parkSummaries.length === 1) {
      setSelectedParkId(parkSummaries[0].park_id);
    }
    // If the selected park no longer exists in the new month's data, reset.
    if (selectedParkId && parkSummaries.length > 0 &&
        !parkSummaries.find((p) => p.park_id === selectedParkId)) {
      setSelectedParkId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkSummaries]);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (selectedParkId && r.park_id !== selectedParkId) return false;
      if (filter !== "all" && r.status !== filter) return false;
      if (!q) return true;
      return [r.employee_code, r.employee_name, r.park_name, r.park_code, r.designation]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q));
    });
  }, [data, filter, search, selectedParkId]);

  // Group by park for cleaner rendering (after selection there is one group)
  const groupedByPark = useMemo(() => {
    const map = new Map();
    for (const r of filteredRows) {
      const key = r.park_id || "_";
      if (!map.has(key)) {
        map.set(key, {
          park_id: r.park_id,
          park_code: r.park_code,
          park_name: r.park_name,
          rows: [],
        });
      }
      map.get(key).rows.push(r);
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.park_code || "").localeCompare(String(b.park_code || "")),
    );
  }, [filteredRows]);

  const counts = data?.counts || { pending: 0, confirmed: 0, disputed: 0, total: 0 };
  const selectedPark = parkSummaries.find((p) => p.park_id === selectedParkId);

  const onConfirmQuick = async (row) => {
    try {
      await api.post(`/pmc/salary-verifications/${row.id}`, {
        action: "confirm",
      });
      toast.success(`${row.employee_name} confirmed as paid`);
      await loadRows(month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm");
    }
  };

  const closeDialog = () => setDialog(null);

  return (
    <AppShell
      title="Salary Verification"
      subtitle={selectedPark ? `${selectedPark.park_code} · ${selectedPark.park_name}` : "Pick a park to verify"}
    >
      <div className="space-y-3 sm:space-y-4">
        {/* Month selector — always visible */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1 min-w-[160px] flex-1">
            <Label className="text-xs">Salary month</Label>
            <Select value={month} onValueChange={(v) => { setMonth(v); setSelectedParkId(""); }} disabled={months.length === 0}>
              <SelectTrigger className="w-full" data-testid="pmc-sv-month-select">
                <SelectValue placeholder={months.length === 0 ? "Nothing published" : "Select month"} />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.month} value={m.month}>
                    {monthLabel(m.month)} {m.pending > 0 ? `· ${m.pending} pending` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedPark && (
            <Button
              variant="outline"
              size="sm"
              className="h-10"
              onClick={() => setSelectedParkId("")}
              data-testid="pmc-sv-change-park"
            >
              Change park
            </Button>
          )}
        </div>

        {/* Empty / loading state */}
        {loading ? (
          <Card>
            <CardContent className="py-6 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </CardContent>
          </Card>
        ) : months.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="pmc-sv-empty">
              <CircleSlash className="mx-auto mb-2 h-6 w-6 text-slate-400" />
              No salary months have been published for verification yet.
              <div className="mt-1 text-[11px]">Once Admin publishes a month, it will appear here.</div>
            </CardContent>
          </Card>
        ) : counts.total === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="pmc-sv-empty-month">
              <CircleSlash className="mx-auto mb-2 h-6 w-6 text-slate-400" />
              No published rows for your parks in {monthLabel(month)}.
            </CardContent>
          </Card>
        ) : !selectedPark ? (
          /* Step 1 — Park selector */
          <div className="space-y-2" data-testid="pmc-sv-park-picker">
            <div className="text-[12px] font-medium text-muted-foreground">
              Pick a park to view its employees
            </div>
            {parkSummaries.map((p) => {
              const dueLabel = p.pending > 0
                ? `${p.pending} to verify`
                : p.disputed > 0
                  ? `${p.disputed} disputed`
                  : "All done";
              const dueTone = p.pending > 0
                ? "bg-amber-50 border-amber-200 text-amber-900"
                : p.disputed > 0
                  ? "bg-rose-50 border-rose-200 text-rose-900"
                  : "bg-emerald-50 border-emerald-200 text-emerald-800";
              return (
                <button
                  key={p.park_id}
                  type="button"
                  onClick={() => setSelectedParkId(p.park_id)}
                  className="block w-full rounded-lg border bg-white p-3 text-left transition-colors hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  data-testid={`pmc-sv-pick-park-${p.park_code}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Wallet className="h-4 w-4 text-amber-700 shrink-0" />
                        <span className="font-mono text-[11px] text-muted-foreground">{p.park_code}</span>
                        <span className="font-medium truncate">{p.park_name}</span>
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {p.total} employee{p.total === 1 ? "" : "s"} &middot; Claimed total: ₹{fmtINR(p.claimed_total)}
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${dueTone}`}>
                      {dueLabel}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-1 text-[11px]">
                    <span className="rounded-md bg-amber-50 px-2 py-1 text-amber-900 text-center">
                      <span className="block text-[9px] uppercase tracking-wide opacity-70">Pending</span>
                      <span className="font-semibold tabular-nums">{p.pending}</span>
                    </span>
                    <span className="rounded-md bg-emerald-50 px-2 py-1 text-emerald-900 text-center">
                      <span className="block text-[9px] uppercase tracking-wide opacity-70">Confirmed</span>
                      <span className="font-semibold tabular-nums">{p.confirmed}</span>
                    </span>
                    <span className="rounded-md bg-rose-50 px-2 py-1 text-rose-900 text-center">
                      <span className="block text-[9px] uppercase tracking-wide opacity-70">Disputed</span>
                      <span className="font-semibold tabular-nums">{p.disputed}</span>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          /* Step 2 — Park selected: show search + filter + rows */
          <>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1 min-w-[140px] flex-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Code or name…"
                    className="pl-7"
                    data-testid="pmc-sv-search"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Status</Label>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[120px] sm:w-[160px]" data-testid="pmc-sv-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="disputed">Disputed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Park-scoped counts */}
            <div className="grid gap-2 grid-cols-2 sm:grid-cols-4" data-testid="pmc-sv-counts">
              <CountTile label="Total" value={selectedPark.total} tone="slate" testId="pmc-sv-count-total" />
              <CountTile label="Pending" value={selectedPark.pending} tone="amber" testId="pmc-sv-count-pending" />
              <CountTile label="Confirmed" value={selectedPark.confirmed} tone="emerald" testId="pmc-sv-count-confirmed" />
              <CountTile label="Disputed" value={selectedPark.disputed} tone="rose" testId="pmc-sv-count-disputed" />
            </div>

            {filteredRows.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="pmc-sv-empty-filter">
                  <CircleSlash className="mx-auto mb-2 h-6 w-6 text-slate-400" />
                  No rows match your filter / search.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {groupedByPark.map((g) => (
                  <Card key={g.park_id} data-testid={`pmc-sv-park-${g.park_code}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-[14px]">
                        <Wallet className="h-4 w-4 text-amber-700" />
                        <span className="font-mono text-[12px] text-slate-500 mr-1">{g.park_code}</span>
                        {g.park_name}
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          {g.rows.length} row{g.rows.length === 1 ? "" : "s"}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                  {/* Mobile: stacked cards */}
                  <div className="divide-y md:hidden">
                    {g.rows.map((r) => (
                      <div key={r.id} className="p-3" data-testid={`pmc-sv-row-${r.employee_code}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{r.employee_name}</div>
                            <div className="text-[11px] text-muted-foreground font-mono">
                              {r.employee_code}{r.is_reliever ? " · Reliever" : ""} · {r.designation || r.category}
                            </div>
                          </div>
                          <StatusBadge status={r.status} />
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
                          <div>
                            <div className="text-[10px] uppercase text-muted-foreground">Days</div>
                            <div className="tabular-nums">{r.payable_days}/{r.total_days}</div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between gap-1">
                              <div className="text-[10px] uppercase text-muted-foreground">Net pay (system) ₹</div>
                              <SalaryBreakdown row={r} testidSuffix={`-mob-${r.employee_code}`} />
                            </div>
                            <div className="tabular-nums font-medium text-emerald-800">
                              {fmtINR(r.system_net_pay)}
                            </div>
                          </div>
                          <div className="col-span-2 grid grid-cols-2 gap-2 rounded-md bg-slate-50 px-2 py-1.5">
                            <div>
                              <div className="text-[10px] uppercase text-muted-foreground">Claimed as paid ₹</div>
                              <div className="tabular-nums">
                                {r.claimed_paid_amount != null
                                  ? fmtINR(r.claimed_paid_amount)
                                  : <span className="text-muted-foreground">Not claimed</span>}
                              </div>
                            </div>
                            {r.status !== "pending" ? (
                              <div>
                                <div className="text-[10px] uppercase text-muted-foreground">You marked ₹</div>
                                <div className="tabular-nums font-medium">
                                  {r.actual_paid_amount != null ? fmtINR(r.actual_paid_amount) : "—"}
                                </div>
                              </div>
                            ) : null}
                          </div>
                          {r.status !== "pending" && r.notes ? (
                            <div className="col-span-2 text-[11px] text-slate-600 italic">Note: {r.notes}</div>
                          ) : null}
                        </div>
                        <div className="mt-2 flex gap-1.5">
                          {r.status === "pending" ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 h-9 border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 text-[12px]"
                                onClick={() => onConfirmQuick(r)}
                                data-testid={`pmc-sv-confirm-${r.employee_code}`}
                              >
                                <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 h-9 border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-100 text-[12px]"
                                onClick={() => setDialog({ row: r, mode: "dispute" })}
                                data-testid={`pmc-sv-dispute-${r.employee_code}`}
                              >
                                <AlertTriangle className="mr-1 h-3.5 w-3.5" /> Different
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 h-9 text-[12px]"
                              onClick={() => setDialog({ row: r, mode: r.status === "disputed" ? "dispute" : "confirm", editing: true })}
                              data-testid={`pmc-sv-edit-${r.employee_code}`}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop / tablet: table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="border-y bg-slate-50/80 text-left text-[10px] uppercase tracking-wide text-slate-500">
                          <th className="py-2 pl-4 pr-2">Employee</th>
                          <th className="py-2 pr-2">Designation</th>
                          <th className="py-2 pr-2 text-right">Days</th>
                          <th className="py-2 pr-2 text-right">Net pay (system) ₹</th>
                          <th className="py-2 pr-2 text-right">Claimed as paid (₹)</th>
                          <th className="py-2 pr-2 text-right">You marked as paid (₹)</th>
                          <th className="py-2 pr-2">Status</th>
                          <th className="py-2 pr-4 text-right w-[220px]">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map((r) => (
                          <tr key={r.id} className="border-b last:border-b-0">
                            <td className="py-2 pl-4 pr-2">
                              <div className="font-medium">{r.employee_name}</div>
                              <div className="text-[11px] text-muted-foreground font-mono">{r.employee_code}{r.is_reliever ? " · Reliever" : ""}</div>
                            </td>
                            <td className="py-2 pr-2 text-slate-700">{r.designation || r.category}</td>
                            <td className="py-2 pr-2 text-right tabular-nums">
                              {r.payable_days}/{r.total_days}
                            </td>
                            <td className="py-2 pr-2 text-right tabular-nums">
                              <div className="flex items-center justify-end gap-1">
                                <span className="font-semibold text-emerald-800">₹{fmtINR(r.system_net_pay)}</span>
                                <SalaryBreakdown row={r} testidSuffix={`-tab-${r.employee_code}`} />
                              </div>
                            </td>
                            <td className="py-2 pr-2 text-right tabular-nums">
                              {r.claimed_paid_amount != null
                                ? fmtINR(r.claimed_paid_amount)
                                : <span className="text-muted-foreground">Not claimed</span>}
                            </td>
                            <td className="py-2 pr-2 text-right tabular-nums">
                              {r.actual_paid_amount != null ? fmtINR(r.actual_paid_amount) : <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="py-2 pr-2"><StatusBadge status={r.status} /></td>
                            <td className="py-2 pr-4 text-right">
                              <div className="flex justify-end gap-1.5">
                                {r.status === "pending" ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 text-[11px] px-2.5"
                                      onClick={() => onConfirmQuick(r)}
                                      data-testid={`pmc-sv-confirm-${r.employee_code}-tab`}
                                    >
                                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Confirm
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-100 text-[11px] px-2.5"
                                      onClick={() => setDialog({ row: r, mode: "dispute" })}
                                      data-testid={`pmc-sv-dispute-${r.employee_code}-tab`}
                                    >
                                      <AlertTriangle className="mr-1 h-3.5 w-3.5" /> Different amount
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-[11px] px-2.5"
                                    onClick={() => setDialog({ row: r, mode: r.status === "disputed" ? "dispute" : "confirm", editing: true })}
                                    data-testid={`pmc-sv-edit-${r.employee_code}-tab`}
                                  >
                                    Edit
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirm / Dispute dialog */}
      <DecisionDialog
        open={!!dialog}
        row={dialog?.row}
        mode={dialog?.mode}
        onClose={closeDialog}
        onDone={async () => { closeDialog(); await loadRows(month); }}
      />
    </AppShell>
  );
}

function CountTile({ label, value, tone = "slate", testId }) {
  const tones = {
    slate:   "border-slate-200   bg-slate-50    text-slate-900",
    amber:   "border-amber-200   bg-amber-50    text-amber-900",
    emerald: "border-emerald-200 bg-emerald-50  text-emerald-900",
    rose:    "border-rose-200    bg-rose-50     text-rose-900",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || tones.slate}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value ?? 0}</div>
    </div>
  );
}

function DecisionDialog({ open, row, mode, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (row) {
      setAmount(row.actual_paid_amount != null ? String(Math.round(row.actual_paid_amount)) : "");
      setNotes(row.notes || "");
    } else {
      setAmount("");
      setNotes("");
    }
  }, [row]);

  if (!row) return null;
  const isDispute = mode === "dispute";

  const submit = async () => {
    const num = parseFloat(amount);
    if (isDispute && (!Number.isFinite(num) || num < 0)) {
      toast.error("Enter a valid paid amount");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/pmc/salary-verifications/${row.id}`, {
        action: isDispute ? "dispute" : "confirm",
        actual_paid_amount: isDispute ? num : (Number.isFinite(num) ? num : null),
        notes: notes || null,
      });
      toast.success(isDispute ? "Reported with different amount" : "Confirmed as paid");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="pmc-sv-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDispute ? <AlertTriangle className="h-4 w-4 text-rose-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            {isDispute ? "Report different paid amount" : "Confirm salary paid"}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            <span className="font-mono">{row.employee_code}</span> · {row.employee_name} · {row.park_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border bg-slate-50 px-3 py-2 text-[12px]">
            <div className="flex items-center gap-1.5 text-slate-600">
              <Coins className="h-3.5 w-3.5" /> Amount claimed as paid by contractor
            </div>
            <div className="text-xl font-semibold tabular-nums text-slate-900">
              {row.claimed_paid_amount != null
                ? <>₹ {fmtINR(row.claimed_paid_amount)}</>
                : <span className="text-base text-muted-foreground">Not in wage register</span>}
            </div>
            <div className="text-[11px] text-slate-500">{row.payable_days}/{row.total_days} days · {row.designation || row.category}</div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[12px]">
              {isDispute ? "Actual amount paid (₹)" : "Confirm amount paid (₹) — leave blank to accept the claim"}
            </Label>
            <Input
              type="number"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={isDispute
                ? "e.g. 2400"
                : (row.claimed_paid_amount != null ? String(Math.round(row.claimed_paid_amount)) : "Enter paid amount")}
              data-testid="pmc-sv-dialog-amount"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[12px]">Notes {isDispute ? "(reason for difference)" : "(optional)"}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={isDispute ? "e.g. Advance adjusted, leave deduction…" : ""}
              data-testid="pmc-sv-dialog-notes"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="pmc-sv-dialog-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className={isDispute ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}
            data-testid="pmc-sv-dialog-submit"
          >
            {busy ? (
              <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Saving</>
            ) : (
              <><Send className="mr-1.5 h-3.5 w-3.5" /> {isDispute ? "Report" : "Confirm"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
